import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '../../../components/ui/Modal';
import { Input, FormField } from '../../../components/ui/Input';
import api from '../../../services/api';

function groupByModule(permissions) {
  return permissions.reduce((acc, p) => {
    if (!acc[p.module]) acc[p.module] = [];
    acc[p.module].push(p);
    return acc;
  }, {});
}

export function RoleModal({ open, onClose, role, allPermissions, onSaved }) {
  const { t, i18n } = useTranslation();
  const isEdit   = Boolean(role);
  const isSystem = role?.isSystem ?? false;
  const lang     = i18n.language;

  const [name, setName]               = useState('');
  const [description, setDescription] = useState('');
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [saving, setSaving]           = useState(false);
  const [error, setError]             = useState('');

  useEffect(() => {
    if (open) {
      setName(role?.name ?? '');
      setDescription(role?.description ?? '');
      setSelectedKeys((role?.permissions ?? []).map((p) => p.key ?? p));
      setError('');
    }
  }, [open, role]);

  const grouped = useMemo(() => groupByModule(allPermissions), [allPermissions]);

  function togglePermission(key) {
    setSelectedKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );
  }

  function toggleModule(module) {
    const keys       = grouped[module].map((p) => p.key);
    const allSelected = keys.every((k) => selectedKeys.includes(k));
    if (allSelected) {
      setSelectedKeys((prev) => prev.filter((k) => !keys.includes(k)));
    } else {
      setSelectedKeys((prev) => [...new Set([...prev, ...keys])]);
    }
  }

  async function handleSave() {
    if (!name.trim()) { setError(t('roles.error_name_required')); return; }
    setSaving(true);
    setError('');
    try {
      if (isEdit) {
        await api.put(`/roles/${role._id}`, {
          name:        isSystem ? undefined : name.trim(),
          description: description.trim() || undefined,
          permissions: selectedKeys,
        });
      } else {
        await api.post('/roles', {
          name:        name.trim(),
          description: description.trim() || undefined,
          permissions: selectedKeys,
        });
      }
      onSaved();
      onClose();
    } catch (e) {
      setError(e?.response?.data?.message ?? t('common.error_occurred'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEdit ? t('roles.edit_role') : t('roles.new_role')}
      size="lg"
      footer={
        <>
          <button
            onClick={onClose}
            disabled={saving}
            className="h-9 px-4 rounded-lg border border-slate-200 text-[13px] font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50 transition"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="h-9 px-4 rounded-lg bg-primary text-white text-[13px] font-semibold hover:opacity-90 disabled:opacity-50 transition"
          >
            {saving ? t('common.saving') : t('common.save')}
          </button>
        </>
      }
    >
      {error && (
        <p className="text-[12px] text-rose-600 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">
          {error}
        </p>
      )}

      <FormField label={t('roles.field_name')} required>
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={isSystem}
          placeholder={t('roles.name_placeholder')}
        />
        {isSystem && (
          <p className="text-[11px] text-slate-400 mt-1">{t('roles.system_name_locked')}</p>
        )}
      </FormField>

      <FormField label={t('roles.field_description')}>
        <Input
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder={t('roles.description_placeholder')}
        />
      </FormField>

      <div>
        <p className="text-[11px] font-semibold text-slate-600 uppercase tracking-wide mb-3">
          {t('roles.permissions')}
        </p>
        <div className="space-y-4 max-h-72 overflow-y-auto pr-1">
          {Object.entries(grouped).map(([module, perms]) => {
            const moduleKeys  = perms.map((p) => p.key);
            const allSelected = moduleKeys.every((k) => selectedKeys.includes(k));
            const someSelected = moduleKeys.some((k) => selectedKeys.includes(k));

            return (
              <div key={module} className="border border-slate-200 rounded-lg overflow-hidden">
                <button
                  type="button"
                  onClick={() => toggleModule(module)}
                  className="w-full flex items-center justify-between px-3 py-2 bg-slate-50 hover:bg-slate-100 transition text-left"
                >
                  <span className="text-[12px] font-semibold text-slate-700 capitalize">
                    {t(`modules.${module}`, { defaultValue: module })}
                  </span>
                  <span className={`w-4 h-4 rounded border flex-shrink-0 flex items-center justify-center transition ${
                    allSelected
                      ? 'bg-primary border-primary'
                      : someSelected
                      ? 'bg-primary/30 border-primary'
                      : 'border-slate-300'
                  }`}>
                    {(allSelected || someSelected) && (
                      <svg viewBox="0 0 10 8" className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" strokeWidth="1.75">
                        {allSelected ? (
                          <path d="M1 4l3 3 5-5" strokeLinecap="round" strokeLinejoin="round" />
                        ) : (
                          <path d="M1 4h8" strokeLinecap="round" />
                        )}
                      </svg>
                    )}
                  </span>
                </button>
                <div className="divide-y divide-slate-100">
                  {perms.map((p) => (
                    <label
                      key={p.key}
                      className="flex items-center gap-2.5 px-3 py-2 cursor-pointer hover:bg-slate-50 transition"
                    >
                      <input
                        type="checkbox"
                        checked={selectedKeys.includes(p.key)}
                        onChange={() => togglePermission(p.key)}
                        className="w-4 h-4 rounded border-slate-300 accent-teal-600 cursor-pointer"
                      />
                      <span className="text-[12.5px] text-slate-700">
                        {lang === 'en' ? p.nameEn : p.name}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </Modal>
  );
}
